import { DEFAULT_MAX_SEARCH_COUNT, MedplumClient, resolveId } from "@medplum/core";
import type { Flag } from "@medplum/fhirtypes";

import { CLIENT_FLAG_IDENTIFIER_SYSTEM } from "@/fhir/constants/identifier";
import { joinReferenceString } from "@/utils/medplum";
import { fetchAllPaginatedResources, PaginatedResult } from "@/utils/pagination";

import { FlagFailure } from "../constants/flag-failures";
import { ACTIVE_FLAG_STATUS } from "../constants/flags";
import { getLibraryFlagId } from "../utils/client-flag";

export type CountActiveFlagsByClientResult =
  | { ok: true; data: Map<string, number> }
  | { ok: false; reason: FlagFailure.LookupFailed };

/**
 * Counts the charts carrying each library entry, keyed by the entry's logical id. The counts feed
 * the flag library's `On Charts` column and the number the delete confirmation warns with.
 *
 * One paged search for the whole library, not one per entry: a practice curating 200 entries would
 * otherwise put 200 requests behind one page of the flag library.
 *
 * Never throws. A count that cannot be read is not worth failing a library page over — the caller
 * gets `undefined` and renders the column as unknown.
 *
 * @param practiceId - Practice Organization logical id, which scopes the search
 * @returns Counts by library entry id, or `undefined` when any page of the search failed
 */
export async function countChartsByLibraryFlag(
  medplum: MedplumClient,
  practiceId: string,
): Promise<Map<string, number> | undefined> {
  const result = await searchActiveAppliedFlags(medplum, practiceId, "extension");

  if (!result.success) return undefined;

  return result.data.reduce<Map<string, number>>((counts, flag) => {
    const libraryFlagId = getLibraryFlagId(flag);

    // An applied flag whose link cannot be read points at no entry to count.
    return libraryFlagId ? counts.set(libraryFlagId, (counts.get(libraryFlagId) ?? 0) + 1) : counts;
  }, new Map());
}

/**
 * Counts the active flags on each chart, keyed by Patient logical id — the number the client
 * list's flag-count sort orders by. A client with no flags is absent from the map.
 *
 * Counts every active applied flag with a readable library link. Flags on deleted entries count
 * too, since the client list keeps those flags and marks them deprecated.
 *
 * @param medplum - Medplum client
 * @param practiceId - Practice Organization logical id, which scopes the search
 * @returns Counts by Patient id, or `LookupFailed` when any page of the search failed. The
 * pagination helper reports that failure to Sentry.
 */
export async function countActiveFlagsByClient(
  medplum: MedplumClient,
  practiceId: string,
): Promise<CountActiveFlagsByClientResult> {
  const result = await searchActiveAppliedFlags(medplum, practiceId, "subject,extension");

  if (!result.success) return { ok: false, reason: FlagFailure.LookupFailed };

  return {
    ok: true,
    data: result.data.reduce<Map<string, number>>((counts, flag) => {
      const clientId = resolveId(flag.subject);

      // A flag with no readable library link is not a client flag to count.
      if (!clientId || !getLibraryFlagId(flag)) return counts;

      return counts.set(clientId, (counts.get(clientId) ?? 0) + 1);
    }, new Map()),
  };
}

/**
 * Every active applied flag in the practice, in one paged search.
 *
 * The search matches only applied flags, by the identifier system every applied flag carries, and
 * asks for only the elements the caller's count reads.
 *
 * Compartment-scoped, per the tenant model. An unscoped search here would count another practice's
 * charts. Uncached, so a count reflects a flag applied moments ago. A page that fails partway
 * through fails the whole search, since the pages before it alone would undercount.
 *
 * @param practiceId - Practice Organization logical id, which scopes the search
 * @param elements - The `_elements` the caller reads
 * @returns The pagination helper's result, which reports its own failures to Sentry
 */
function searchActiveAppliedFlags(
  medplum: MedplumClient,
  practiceId: string,
  elements: string,
): Promise<PaginatedResult<Flag[]>> {
  return fetchAllPaginatedResources<Flag>(
    medplum,
    "Flag",
    {
      _compartment: joinReferenceString("Organization", practiceId)!,
      // `system|` with no value matches any identifier under the system.
      identifier: `${CLIENT_FLAG_IDENTIFIER_SYSTEM}|`,
      status: ACTIVE_FLAG_STATUS,
      _elements: elements,
      _count: String(DEFAULT_MAX_SEARCH_COUNT),
    },
    { throwOnPageError: true },
  );
}
